// Data Source API service functions
import { buildDataApiUrl } from '@api/api';

const getHeaders = () => ({
  'Content-Type': 'application/json',
});

export const fetchAvailableTables = async () => {
  try {
    const response = await fetch(buildDataApiUrl('/data-sources/tables'), {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    return {
      status: 'success',
      data,
    };
  } catch (error) {

    return {
      status: 'error',
      message: `Failed to connect to data API: ${error.message}`,
    };
  }
};

export const fetchTableColumns = async (tableName) => {
  try {
    const response = await fetch(buildDataApiUrl(`/data-sources/tables/${tableName}/columns`), {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: `Failed to fetch columns for ${tableName}: ${error.message}`,
    };
  }
};

export const validateQuery = async (query) => {
  try {
    // Basic client-side validation before calling backend
    if (!query || query.trim().length === 0) {
      return {
        status: 'error',
        data: {
          isValid: false,
          valid: false,
          error: 'Query cannot be empty',
          columns: []
        }
      };
    }

    const lowerQuery = query.toLowerCase();
    if (!lowerQuery.includes('select')) {
      return {
        status: 'error',
        data: {
          isValid: false,
          valid: false,
          error: 'Only SELECT queries are allowed',
          columns: []
        }
      };
    }

    // Call backend API to validate and execute query
    const response = await fetch(buildDataApiUrl('/data-sources/validate-query'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ query }),
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        status: 'error',
        data: {
          isValid: false,
          valid: false,
          error: result.message || 'Failed to validate query',
          columns: []
        }
      };
    }

    // Backend returns { columns, data, isValid, error? }
    if (result.isValid) {
      return {
        status: 'success',
        data: {
          isValid: true,
          valid: true,
          message: 'Query is valid',
          columns: result.columns || [],
          sampleData: result.data || []
        }
      };
    }

    return {
      status: 'error',
      data: {
        isValid: false,
        valid: false,
        error: result.error || 'Query validation failed',
        columns: []
      }
    };
  } catch (error) {
    return {
      status: 'error',
      message: `Query validation failed: ${error.message}`,
    };
  }
};

/**
 * Fetch widget data with server-side query building
 *
 * This is the secure way to fetch widget data - the query is built on the server
 * with proper CTE Row-Level Security filtering applied.
 *
 * @param {string} widgetType - Widget type (bar, pie, line, table, metric, etc.)
 * @param {object} config - Widget configuration
 * @param {object} dashboardFilters - Dashboard-level filters (time, entities, status)
 * @param {boolean} isEditMode - Whether in edit mode (applies LIMIT 1000)
 * @param {number} previewLimit - Optional preview row limit (deprecated, use page/pageSize)
 * @param {object} queryParameters - Query parameters to replace {{PARAM}} placeholders
 * @param {number} page - Page number (1-indexed) for server-side pagination
 * @param {number} pageSize - Number of rows per page for server-side pagination
 * @returns {Promise<Object>} Response with widget data including total count
 */
// Clean dataSource configs before sending to backend
// - Removes empty string values (backend expects omitted fields, not "")
// - Converts categoryLimit from string to number
const cleanConfig = (config) => {
  if (!config?.dataSources?.length) return config;

  return {
    ...config,
    dataSources: config.dataSources.map((ds) => {
      if (!ds.config) return ds;

      const dsConfig = { ...ds.config };

      // Remove empty string values
      Object.keys(dsConfig).forEach((key) => {
        if (dsConfig[key] === '') delete dsConfig[key];
      });

      // Convert categoryLimit to number
      if (typeof dsConfig.categoryLimit === 'string') {
        const num = parseInt(dsConfig.categoryLimit, 10);
        if (!Number.isNaN(num)) dsConfig.categoryLimit = num;
        else delete dsConfig.categoryLimit;
      }

      return { ...ds, config: dsConfig };
    }),
  };
};

export const fetchWidgetData = async (
  widgetType,
  config,
  dashboardFilters = {},
  isEditMode = false,
  previewLimit = null,
  queryParameters = {},
  page = null,
  pageSize = null
) => {
  try {
    // SECURITY: Send parameters separately to backend for secure replacement
    // Do NOT replace parameters client-side to prevent SQL injection
    const requestBody = {
      widgetType,
      config: cleanConfig(config),
      dashboardFilters,
      isEditMode,
    };

    if (previewLimit) {
      requestBody.previewLimit = previewLimit;
    }

    // Add pagination parameters for server-side pagination
    if (page !== null && pageSize !== null) {
      requestBody.page = page;
      requestBody.pageSize = pageSize;
    }

    // Send queryParameters separately for backend validation and secure replacement
    if (queryParameters && Object.keys(queryParameters).length > 0) {
      requestBody.queryParameters = queryParameters;
    }

    const response = await fetch(buildDataApiUrl('/data-sources/widget-data'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(requestBody),
    });

    if (response.ok) {
      const responseBody = await response.json();

      // Handle new paginated response format: { data: [], total: number }
      if (responseBody && !Array.isArray(responseBody) && responseBody.data !== undefined) {
        const { data, total } = responseBody;

        if (Array.isArray(data) && data.length > 0) {
          const columns = Object.keys(data[0]);
          const rows = data.map(row => columns.map(col => row[col]));

          return {
            status: 'success',
            data: {
              columns,
              rows,
              total: total ?? data.length,
              message: `Query returned ${rows.length} rows of ${total ?? rows.length} total`
            },
          };
        }

        return {
          status: 'success',
          data: {
            columns: [],
            rows: [],
            total: total ?? 0,
            message: 'Query returned no results'
          },
        };
      }

      // No valid data in response
      return {
        status: 'success',
        data: {
          columns: [],
          rows: [],
          total: 0,
          message: 'Query returned no results'
        },
      };
    }

    // Handle timeout errors (408)
    if (response.status === 408) {
      const error = new Error('QUERY_TIMEOUT_ERROR');
      error.statusCode = 408;
      throw error;
    }

    throw new Error(`API returned ${response.status}: ${response.statusText}`);
  } catch (error) {
    return {
      status: 'error',
      message: error.message || 'Failed to fetch widget data',
      data: {
        columns: [],
        rows: [],
        total: 0,
        message: 'No data available'
      }
    };
  }
};

/**
 * Fetch timeseries data for given entity IDs or MUIDs
 * @param {Array<number>|Array<string>} idsOrMuids - Array of entity IDs or MUIDs
 * @param {Object} options - Optional parameters
 * @param {string} options.timeFrom - Start time (ISO string)
 * @param {string} options.timeTo - End time (ISO string)
 * @param {boolean} options.isHost - Whether entities are hosts (true) or metrics (false)
 * @param {boolean} options.useMuid - Whether to use MUID instead of ID
 * @param {string} options.aggregateLiteral - Time grouping interval (e.g., '5m', '1h')
 * @param {string} options.aggregateFunction - Aggregation function (mean, max, min, sum, count)
 * @returns {Promise<Object>} Response with timeseries data per ID
 */
/**
 * Fetch available inventory categories from Elasticsearch
 * Used in the Inventory Widget configuration
 * @returns {Promise<Object>} Response with array of category names
 */
export const fetchInventoryCategories = async () => {
  try {
    const response = await fetch(buildDataApiUrl('/inventory/categories'), {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data: data.categories || [],
    };
  } catch (error) {
    return {
      status: 'error',
      message: `Failed to fetch inventory categories: ${error.message}`,
      data: [],
    };
  }
};

/**
 * Fetch inventory widget data based on configuration
 * @param {Object} config - Widget configuration
 * @param {string} config.rowType - 'hosts' or 'items'
 * @param {string} config.tableLayout - Layout type
 * @param {string} config.categoryHandling - How to handle categories
 * @param {Array<string>} config.selectedCategories - Selected category names
 * @param {string} config.scanBasis - 'latest' or other scan basis
 * @param {number} config.pivotColumnLimit - Max columns for pivot layout
 * @param {Object} dashboardFilters - Dashboard filters (entities, time range)
 * @returns {Promise<Object>} Response with inventory data
 */
export const fetchInventoryWidgetData = async (config, dashboardFilters = {}) => {
  try {
    // Map dashboard filters to the backend expected format
    // Dashboard filters have: entities.hosts, entities.host_groups, entities.business_services
    // Backend expects: filters.hosts, filters.host_groups, filters.business_services
    const mappedFilters = {
      hosts: dashboardFilters?.entities?.hosts || [],
      host_groups: dashboardFilters?.entities?.host_groups || [],
      business_services: dashboardFilters?.entities?.business_services || [],
      hostCategories: dashboardFilters?.hostCategories || [],
      hostStatuses: dashboardFilters?.hostStatuses || [],
    };

    const requestBody = {
      rowType: config.inventoryRowType || 'hosts',
      tableLayout: config.inventoryTableLayout || 'long_form',
      categoryHandling: config.inventoryCategoryHandling || 'column',
      selectedCategories: config.inventorySelectedCategories || [],
      scanBasis: config.inventoryScanBasis || 'latest',
      pivotColumnLimit: config.inventoryPivotColumnLimit || 20,
      filters: mappedFilters,
    };

    const response = await fetch(buildDataApiUrl('/inventory/widget-data'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: `Failed to fetch inventory widget data: ${error.message}`,
      data: {
        columns: [],
        rows: [],
        total: 0,
      },
    };
  }
};

/**
 * Fetch sample inventory data for preview mode.
 * Similar to timeseries/sample - returns a limited set of random hosts with inventory data.
 * RLS/permissions are applied on the backend.
 *
 * @param {Object} config - Inventory widget configuration
 * @param {string} config.inventoryRowType - 'hosts' or 'items'
 * @param {string} config.inventoryTableLayout - Table layout (long_form, split_by_category, pivot, etc.)
 * @param {string} config.inventoryCategoryHandling - 'column' or 'specific'
 * @param {Array<string>} config.inventorySelectedCategories - Selected category names
 * @param {string} config.inventoryScanBasis - 'latest' or other scan basis
 * @param {number} config.inventoryPivotColumnLimit - Max columns for pivot layout
 * @param {number} limit - Number of sample hosts to return (1-10, default 3)
 * @returns {Promise<Object>} Response with sample inventory data
 */
export const fetchInventorySample = async (config, limit = 3) => {
  try {
    const requestBody = {
      limit,
      rowType: config.inventoryRowType || 'hosts',
      tableLayout: config.inventoryTableLayout || 'long_form',
      categoryHandling: config.inventoryCategoryHandling || 'column',
      selectedCategories: config.inventorySelectedCategories || [],
      scanBasis: config.inventoryScanBasis || 'latest',
      pivotColumnLimit: config.inventoryPivotColumnLimit || 20,
    };

    const response = await fetch(buildDataApiUrl('/inventory/sample'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: `Failed to fetch inventory sample data: ${error.message}`,
      data: {
        hosts: [],
        columns: [],
        rows: [],
        total: 0,
      },
    };
  }
};

export const fetchTimeseries = async (idsOrMuids = [], options = {}) => {
  try {
    if (!idsOrMuids || idsOrMuids.length === 0) {
      return {
        status: 'success',
        data: {},
      };
    }

    // Build request body
    const requestBody = {};

    // Determine if we're using MUIDs or IDs
    const useMuid = options.useMuid || (idsOrMuids.length > 0 && typeof idsOrMuids[0] === 'string');

    if (useMuid) {
      requestBody.muid = idsOrMuids;
    } else {
      requestBody.id = idsOrMuids;
    }

    // Add is_host flag if provided
    if (options.isHost !== undefined) {
      requestBody.is_host = options.isHost;
    }

    // Add time range if provided (timeFrom/timeTo are already Unix seconds)
    if (options.timeFrom || options.timeTo) {
      requestBody.range = {};
      if (options.timeFrom) {
        requestBody.range.start_time = options.timeFrom;
      }
      if (options.timeTo) {
        requestBody.range.end_time = options.timeTo;
      }
    }

    // Add aggregation parameters if provided
    if (options.aggregateLiteral) {
      requestBody.aggregate_literal = options.aggregateLiteral;
    }
    if (options.aggregateFunction) {
      requestBody.aggregate_function = options.aggregateFunction;
    }

    const response = await fetch(buildDataApiUrl('/timeseries'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: `Failed to fetch timeseries data: ${error.message}`,
      data: {},
    };
  }
};

/**
 * Fetch sample metrics/hosts that have recent data in InfluxDB
 * Used for dashboard widget previews - gets entities that actually have data
 * instead of randomly selecting from all entities
 *
 * @param {Object} options - Request options
 * @param {boolean} [options.isHost=false] - Whether to query hosts (true) or metrics (false)
 * @param {number} [options.limit=3] - Number of sample entities to return (max 10)
 * @param {string} [options.lookback='24h'] - Time range to check for data (InfluxDB duration format)
 * @param {string} [options.aggregateLiteral='5m'] - Aggregation interval for timeseries data
 * @param {string} [options.aggregateFunction='mean'] - Aggregation function (mean, max, min, sum)
 * @returns {Promise<Object>} Response with entities and their timeseries data
 */
export const fetchTimeseriesSample = async (options = {}) => {
  try {
    const requestBody = {
      is_host: options.isHost || false,
      limit: options.limit || 3,
      lookback: options.lookback || '24h',
      aggregate_literal: options.aggregateLiteral || '5m',
      aggregate_function: options.aggregateFunction || 'mean',
    };

    const response = await fetch(buildDataApiUrl('/timeseries/sample'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: `Failed to fetch timeseries sample: ${error.message}`,
      data: {
        entities: [],
        timeseries: {},
        entityType: options.isHost ? 'hosts' : 'metrics',
      },
    };
  }
};
